/**
 * AddSensorDialog.jsx - Dialog pour ajouter un nouveau capteur
 *
 * Permet de saisir:
 * - Numéro de série (source_id) - obligatoire
 * - Nom (optionnel)
 * - Secteur (liste déroulante)
 * - Localisation (GPS, clic carte, saisie manuelle)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close, MyLocation, LocationOn } from '@mui/icons-material';
import FarmMapService from './FarmMapService';

/**
 * Dialog pour ajouter un capteur
 */
const AddSensorDialog = ({
  open,
  onClose,
  farmId,
  sectors = [],
  onSensorAdded,
  initialLat = null,
  initialLng = null
}) => {
  // État du formulaire
  const [formData, setFormData] = useState({
    source_id: '',
    sensor_name: '',
    sector_id: '',
    gps_latitude: initialLat || '',
    gps_longitude: initialLng || ''
  });

  // État de l'interface
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Réinitialiser le formulaire quand le dialog s'ouvre
  useEffect(() => {
    if (open) {
      setFormData({
        source_id: '',
        sensor_name: '',
        sector_id: '',
        gps_latitude: initialLat || '',
        gps_longitude: initialLng || ''
      });
      setError('');
    }
  }, [open, initialLat, initialLng]);

  /**
   * Gérer les changements de champ
   */
  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError('');
  };

  /**
   * Obtenir la position GPS de l'utilisateur
   */
  const handleGetLocation = async () => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée par ce navigateur');
      return;
    }

    setIsGettingLocation(true);
    setError('');

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      setFormData(prev => ({
        ...prev,
        gps_latitude: position.coords.latitude.toFixed(6),
        gps_longitude: position.coords.longitude.toFixed(6)
      }));
    } catch (err) {
      setError(`Impossible d'obtenir la position: ${err.message}`);
    } finally {
      setIsGettingLocation(false);
    }
  };

  /**
   * Valider le formulaire
   */
  const validateForm = () => {
    if (!formData.source_id.trim()) {
      setError('Le numéro de série est obligatoire');
      return false;
    }

    // Validation format numéro de série (alphanumérique, 3-50 caractères)
    const serialRegex = /^[a-zA-Z0-9\-_]{3,50}$/;
    if (!serialRegex.test(formData.source_id.trim())) {
      setError('Numéro de série invalide (3-50 caractères alphanumériques, tirets, underscores)');
      return false;
    }

    // Validation coordonnées si fournies
    if (formData.gps_latitude || formData.gps_longitude) {
      const lat = parseFloat(formData.gps_latitude);
      const lng = parseFloat(formData.gps_longitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setError('Latitude invalide (-90 à 90)');
        return false;
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setError('Longitude invalide (-180 à 180)');
        return false;
      }
    }

    return true;
  };

  /**
   * Soumettre le formulaire
   */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const sensorData = {
        source_id: formData.source_id.trim(),
        farm_id: farmId,  // Backend attend snake_case
        sensor_name: formData.sensor_name.trim() || undefined,
        sensor_type: formData.sensor_type || undefined,
        sector_id: formData.sector_id || undefined,
        gps_latitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : undefined,
        gps_longitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : undefined,
        metadata: {
          created_at: new Date().toISOString(),
          created_via: 'map_interface'
        }
      };

      const createdSensor = await FarmMapService.createSensor(sensorData);
      
      // Notifier le parent
      if (onSensorAdded) {
        onSensorAdded(createdSensor);
      }

      // Fermer le dialog
      onClose();
      
    } catch (err) {
      console.error('Erreur création capteur:', err);
      setError(err.message || 'Erreur lors de la création du capteur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 3000 }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Ajouter un capteur</Typography>
          <IconButton onClick={onClose} disabled={loading} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Numéro de série */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Numéro de série *"
              value={formData.source_id}
              onChange={handleChange('source_id')}
              placeholder="Ex: 210506960"
              disabled={loading}
              helperText="Identifiant unique du capteur (3-50 caractères alphanumériques)"
              required
            />
          </Grid>

          {/* Nom du capteur */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nom (optionnel)"
              value={formData.sensor_name}
              onChange={handleChange('sensor_name')}
              placeholder="Ex: Capteur température serre 1"
              disabled={loading}
            />
          </Grid>

          {/* Secteur */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="sector-select-label">Secteur (optionnel)</InputLabel>
              <Select
                labelId="sector-select-label"
                value={String(formData.sector_id)}
                label="Secteur (optionnel)"
                onChange={handleChange('sector_id')}
                disabled={loading || sectors.length === 0}
              >
                <MenuItem value="">
                  <em>Aucun secteur</em>
                </MenuItem>
                {sectors.map((sector) => (
                  <MenuItem key={sector.sector_id} value={String(sector.sector_id)}>
                    {sector.name || `Secteur ${sector.sector_id}`}
                    {sector.compartment_name && ` - ${sector.compartment_name}`}
                  </MenuItem>
                ))}
              </Select>
              {sectors.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Aucun secteur disponible
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Coordonnées GPS */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Localisation GPS (optionnel)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MyLocation />}
                onClick={handleGetLocation}
                disabled={loading || isGettingLocation}
              >
                {isGettingLocation ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Obtention...
                  </>
                ) : (
                  'Utiliser ma position'
                )}
              </Button>
              <Typography variant="caption" color="text.secondary">
                ou saisir manuellement
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Latitude"
              value={formData.gps_latitude}
              onChange={handleChange('gps_latitude')}
              placeholder="Ex: 35.088056"
              disabled={loading}
              type="number"
              inputProps={{ step: "0.000001" }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Longitude"
              value={formData.gps_longitude}
              onChange={handleChange('gps_longitude')}
              placeholder="Ex: -6.108250"
              disabled={loading}
              type="number"
              inputProps={{ step: "0.000001" }}
            />
          </Grid>

          {/* Aide */}
          <Grid item xs={12}>
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 <strong>Conseil:</strong> Vous pouvez laisser les coordonnées vides et placer le capteur directement sur la carte après création.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.source_id.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <LocationOn />}
        >
          {loading ? 'Création...' : 'Créer le capteur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSensorDialog;